import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useTheme } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Zap, TrendingUp, TrendingDown, Target,
  SlidersHorizontal, ShieldCheck, AlertTriangle,
  RefreshCw, Check, X
} from "lucide-react";
import { toast } from "sonner";

// Real-time Risk-to-Reward calculator helper
const calculateRR = (entry, sl, target, zoneType) => {
  const e = parseFloat(entry);
  const s = parseFloat(sl);
  const t = parseFloat(target);
  if (isNaN(e) || isNaN(s) || isNaN(t) || e <= 0 || s <= 0 || t <= 0) {
    return { rr: "—", risk: 0, reward: 0, riskPct: "0.00", rewardPct: "0.00", isValid: true, error: "" };
  }

  const isDemand = (zoneType || "DEMAND").toUpperCase() === "DEMAND";
  const risk = isDemand ? e - s : s - e;
  const reward = isDemand ? t - e : e - t;
  const riskPct = e > 0 ? (risk / e) * 100 : 0;
  const rewardPct = e > 0 ? (reward / e) * 100 : 0;

  if (risk <= 0 || reward <= 0) {
    return {
      rr: "Invalid",
      risk: Math.max(0, risk).toFixed(2),
      reward: Math.max(0, reward).toFixed(2),
      riskPct: riskPct.toFixed(2),
      rewardPct: rewardPct.toFixed(2),
      isValid: false,
      error: isDemand
        ? "For Demand (Long): Stop Loss must be < Entry, and Target must be > Entry"
        : "For Supply (Short): Stop Loss must be > Entry, and Target must be < Entry"
    };
  }

  const rrRatio = (reward / risk).toFixed(1);
  return {
    rr: `${rrRatio}:1`,
    risk: risk.toFixed(2),
    reward: reward.toFixed(2),
    riskPct: riskPct.toFixed(2),
    rewardPct: rewardPct.toFixed(2),
    isValid: true,
    error: ""
  };
};

export default function EditAlertModal({ isOpen, alert, onClose, onSuccess }) {
  const { theme } = useTheme();
  const isLight = theme === "light";

  const [form, setForm] = useState({
    entry: "",
    stop_loss: "",
    target: "",
    zone_type: "DEMAND",
    trade_type: "LONG",
    timeframe: "15 Min",
    score: "8.5",
    setup_type: "MIT",
    admin_remark: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !alert) return;

    setForm({
      entry: alert.entry != null ? String(alert.entry) : "",
      stop_loss: alert.stop_loss != null ? String(alert.stop_loss) : "",
      target: alert.target != null ? String(alert.target) : "",
      zone_type: alert.zone_type || "DEMAND",
      trade_type: alert.trade_type || (alert.zone_type === "SUPPLY" ? "SHORT" : "LONG"),
      timeframe: alert.timeframe || "15 Min",
      score: alert.score != null ? String(alert.score) : "8.5",
      setup_type: alert.setup_type || "MIT",
      admin_remark: alert.admin_remark || ""
    });
  }, [isOpen, alert]);

  const computedRR = useMemo(() => {
    return calculateRR(form.entry, form.stop_loss, form.target, form.zone_type);
  }, [form.entry, form.stop_loss, form.target, form.zone_type]);

  const alertId = alert?.alert_id || alert?.id;
  const isAlreadyApproved = alert?.status === "approved";

  // Handle Save (Draft or Live Level Update)
  const handleSave = async (andApprove = false) => {
    if (!alert || !alertId) return;

    if (!computedRR.isValid) {
      toast.error("Invalid trading levels", { description: computedRR.error });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        entry: parseFloat(form.entry),
        stop_loss: parseFloat(form.stop_loss),
        target: parseFloat(form.target),
        zone_type: form.zone_type,
        trade_type: form.zone_type === "DEMAND" ? "LONG" : "SHORT",
        timeframe: form.timeframe,
        score: form.score,
        setup_type: form.setup_type,
        admin_remark: form.admin_remark,
        remark: form.admin_remark,
        rr: computedRR.rr
      };

      const endpoint = andApprove && !isAlreadyApproved
        ? `${API}/drishti/alerts/${alertId}/approve`
        : `${API}/drishti/alerts/${alertId}/update`;

      const res = await axios.put(endpoint, payload, { withCredentials: true });

      const updatedAlert = res.data?.alert || { ...alert, ...payload };
      toast.success(
        andApprove && !isAlreadyApproved
          ? `⚡ ${alert.script_name} updated & published live!`
          : `✓ ${alert.script_name} setup levels updated successfully!`,
        {
          description: `Entry: ₹${payload.entry} | SL: ₹${payload.stop_loss} | Target: ₹${payload.target} | RR: ${computedRR.rr}`
        }
      );

      onSuccess?.(updatedAlert);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update alert levels");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !alert) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={`max-w-xl p-5 sm:p-6 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto overscroll-contain transition-all border ${
        isLight
          ? "bg-white border-slate-200 text-slate-900 shadow-slate-300/40"
          : "bg-[#0D0D14] border-white/10 text-white shadow-black/80"
      }`}>
        <DialogHeader className={`border-b pb-3 ${isLight ? "border-slate-100" : "border-white/10"}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                form.zone_type === "DEMAND"
                  ? (isLight ? "bg-emerald-50 text-emerald-700 border border-emerald-300" : "bg-[#00FF88]/15 text-[#00FF88] border border-[#00FF88]/30")
                  : (isLight ? "bg-rose-50 text-rose-700 border border-rose-300" : "bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/30")
              }`}>
                {form.zone_type === "DEMAND" ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
              </div>
              <div>
                <DialogTitle className={`text-lg font-bold font-outfit tracking-tight flex items-center gap-2 ${
                  isLight ? "text-slate-900" : "text-white"
                }`}>
                  <span>{alert.script_name}</span>
                  <Badge className={`border-0 text-[10px] font-mono ${
                    isLight ? "bg-slate-100 text-slate-700" : "bg-white/10 text-white"
                  }`}>NSE</Badge>
                  <Badge className={`border-0 text-[10px] font-bold uppercase ${
                    form.zone_type === "DEMAND"
                      ? (isLight ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-[#00FF88]/20 text-[#00FF88]")
                      : (isLight ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-[#FF3B30]/20 text-[#FF3B30]")
                  }`}>
                    {form.zone_type}
                  </Badge>
                  {isAlreadyApproved && (
                    <Badge className={`text-[10px] font-bold border ${
                      isLight
                        ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                        : "bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/25"
                    }`}>
                      Live
                    </Badge>
                  )}
                </DialogTitle>
                <p className={`text-[11px] font-plex ${isLight ? "text-slate-500" : "text-[#94A3B8]"}`}>
                  Directly customize Entry, Stop Loss, Target, and Admin Remark.
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-1.5 rounded-full transition-colors cursor-pointer ${
                isLight ? "bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800" : "bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white"
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Zone & Timeframe Quick Toggles */}
          <div className={`grid grid-cols-2 gap-3 p-3 rounded-xl border ${
            isLight ? "bg-slate-50 border-slate-200" : "bg-white/[0.02] border-white/[0.06]"
          }`}>
            <div>
              <Label className={`text-[11px] font-mono uppercase ${isLight ? "text-slate-600 font-semibold" : "text-[#94A3B8]"}`}>
                Zone Direction
              </Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1">
                <button
                  type="button"
                  onClick={() => setForm(p => ({ ...p, zone_type: "DEMAND", trade_type: "LONG" }))}
                  className={`py-1.5 text-xs font-bold font-outfit rounded-lg border transition-all cursor-pointer ${
                    form.zone_type === "DEMAND"
                      ? (isLight
                          ? "bg-emerald-500 text-white border-emerald-600 shadow-sm"
                          : "bg-[#00FF88]/20 text-[#00FF88] border-[#00FF88]/50 shadow-sm")
                      : (isLight
                          ? "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"
                          : "bg-white/5 text-gray-400 border-white/5 hover:bg-white/10")
                  }`}
                >
                  DEMAND (Long)
                </button>
                <button
                  type="button"
                  onClick={() => setForm(p => ({ ...p, zone_type: "SUPPLY", trade_type: "SHORT" }))}
                  className={`py-1.5 text-xs font-bold font-outfit rounded-lg border transition-all cursor-pointer ${
                    form.zone_type === "SUPPLY"
                      ? (isLight
                          ? "bg-rose-500 text-white border-rose-600 shadow-sm"
                          : "bg-[#FF3B30]/20 text-[#FF3B30] border-[#FF3B30]/50 shadow-sm")
                      : (isLight
                          ? "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"
                          : "bg-white/5 text-gray-400 border-white/5 hover:bg-white/10")
                  }`}
                >
                  SUPPLY (Short)
                </button>
              </div>
            </div>

            <div>
              <Label className={`text-[11px] font-mono uppercase ${isLight ? "text-slate-600 font-semibold" : "text-[#94A3B8]"}`}>
                Timeframe
              </Label>
              <select
                value={form.timeframe}
                onChange={e => setForm(p => ({ ...p, timeframe: e.target.value }))}
                className={`w-full rounded-lg p-1.5 text-xs mt-1 h-8 font-mono border focus:outline-none transition-colors ${
                  isLight
                    ? "bg-white border-slate-300 text-slate-900 focus:border-purple-500"
                    : "bg-[#181824] border-white/10 text-white focus:border-[#00FF88]"
                }`}
              >
                <option value="5 Min">5 Min</option>
                <option value="15 Min">15 Min</option>
                <option value="1H">1 Hour</option>
                <option value="Daily">Daily (1D)</option>
                <option value="Weekly">Weekly (1W)</option>
              </select>
            </div>
          </div>

          {/* 3 Core Trade Levels Grid */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className={`text-xs font-bold font-outfit uppercase tracking-wider flex items-center gap-1.5 ${
                isLight ? "text-slate-700" : "text-[#CBD5E1]"
              }`}>
                <SlidersHorizontal className="w-3.5 h-3.5 text-purple-600 dark:text-[#00F0FF]" /> Setup Levels
              </span>
              <span className={`text-[11px] font-mono ${isLight ? "text-slate-500" : "text-gray-400"}`}>
                Recalibrate Entry, SL &amp; Target
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Entry Price */}
              <div className={`p-3 rounded-xl border transition-colors ${
                isLight
                  ? "bg-slate-50 border-slate-200 focus-within:border-slate-400"
                  : "bg-white/[0.03] border-white/[0.08] focus-within:border-white/30"
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <Label className={`text-[11px] font-mono uppercase ${isLight ? "text-slate-600 font-semibold" : "text-gray-400"}`}>
                    Entry Price
                  </Label>
                  <span className={`text-[10px] font-mono font-bold ${isLight ? "text-sky-600" : "text-[#00F0FF]"}`}>
                    ₹ Level
                  </span>
                </div>
                <Input
                  type="number"
                  step="0.05"
                  value={form.entry}
                  onChange={e => setForm(p => ({ ...p, entry: e.target.value }))}
                  className={`text-base font-bold font-mono h-9 px-2.5 transition-all ${
                    isLight
                      ? "bg-white border-slate-300 text-slate-900 focus:border-purple-500"
                      : "bg-[#12121A] border-white/10 text-white focus:border-[#00F0FF]"
                  }`}
                  placeholder="0.00"
                />
                <p className={`text-[10px] font-mono mt-1 ${isLight ? "text-slate-500" : "text-gray-500"}`}>Trigger Price</p>
              </div>

              {/* Stop Loss */}
              <div className={`p-3 rounded-xl border transition-colors ${
                isLight
                  ? "bg-rose-50/50 border-rose-200 focus-within:border-rose-400"
                  : "bg-[#FF3B30]/[0.03] border-[#FF3B30]/20 focus-within:border-[#FF3B30]/50"
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-[11px] font-mono text-rose-600 dark:text-[#FF3B30] uppercase font-semibold">
                    Stop Loss (SL)
                  </Label>
                  <span className="text-[10px] font-mono text-rose-600 dark:text-[#FF3B30] font-bold">
                    {computedRR.risk > 0 ? `-${computedRR.riskPct}%` : "₹ SL"}
                  </span>
                </div>
                <Input
                  type="number"
                  step="0.05"
                  value={form.stop_loss}
                  onChange={e => setForm(p => ({ ...p, stop_loss: e.target.value }))}
                  className={`text-base font-bold font-mono h-9 px-2.5 transition-all text-rose-600 dark:text-[#FF3B30] ${
                    isLight
                      ? "bg-white border-rose-300 focus:border-rose-500"
                      : "bg-[#12121A] border-[#FF3B30]/30 focus:border-[#FF3B30]"
                  }`}
                  placeholder="0.00"
                />
                <p className="text-[10px] text-rose-600/80 dark:text-[#FF3B30]/70 font-mono mt-1 font-medium">
                  Risk: ₹{computedRR.risk} ({computedRR.riskPct}%)
                </p>
              </div>

              {/* Target Price */}
              <div className={`p-3 rounded-xl border transition-colors ${
                isLight
                  ? "bg-emerald-50/50 border-emerald-200 focus-within:border-emerald-400"
                  : "bg-[#00FF88]/[0.03] border-[#00FF88]/20 focus-within:border-[#00FF88]/50"
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-[11px] font-mono text-emerald-600 dark:text-[#00FF88] uppercase font-semibold">
                    Target Price
                  </Label>
                  <span className="text-[10px] font-mono text-emerald-600 dark:text-[#00FF88] font-bold">
                    {computedRR.reward > 0 ? `+${computedRR.rewardPct}%` : "₹ Target"}
                  </span>
                </div>
                <Input
                  type="number"
                  step="0.05"
                  value={form.target}
                  onChange={e => setForm(p => ({ ...p, target: e.target.value }))}
                  className={`text-base font-bold font-mono h-9 px-2.5 transition-all text-emerald-600 dark:text-[#00FF88] ${
                    isLight
                      ? "bg-white border-emerald-300 focus:border-emerald-500"
                      : "bg-[#12121A] border-[#00FF88]/30 focus:border-[#00FF88]"
                  }`}
                  placeholder="0.00"
                />
                <p className="text-[10px] text-emerald-600/80 dark:text-[#00FF88]/70 font-mono mt-1 font-medium">
                  Reward: ₹{computedRR.reward} (+{computedRR.rewardPct}%)
                </p>
              </div>
            </div>
          </div>

          {/* Dynamic Risk-Reward Bar Visualizer */}
          <div className={`p-3.5 rounded-xl border space-y-2 ${
            isLight ? "bg-slate-50 border-slate-200" : "bg-white/[0.02] border-white/[0.08]"
          }`}>
            <div className="flex items-center justify-between">
              <span className={`text-xs font-bold font-outfit uppercase tracking-wider flex items-center gap-1.5 ${
                isLight ? "text-slate-700" : "text-gray-400"
              }`}>
                <Target className="w-3.5 h-3.5 text-amber-500 dark:text-[#FFCC00]" /> Risk : Reward Gauge
              </span>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-bold font-mono px-2 py-0.5 rounded-md ${
                  computedRR.isValid
                    ? (isLight ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-[#FFCC00]/15 text-[#FFCC00] border border-[#FFCC00]/30")
                    : (isLight ? "bg-rose-100 text-rose-800 border border-rose-300" : "bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/30")
                }`}>
                  {computedRR.rr}
                </span>
              </div>
            </div>

            {computedRR.isValid ? (
              <div>
                <div className={`h-2.5 w-full rounded-full overflow-hidden flex ${
                  isLight ? "bg-slate-200" : "bg-white/5"
                }`}>
                  <div
                    style={{ width: `${Math.max(10, Math.min(50, 100 / (1 + parseFloat(computedRR.rr.split(':')[0] || 1))))}%` }}
                    className="bg-rose-500 dark:bg-[#FF3B30] h-full transition-all"
                    title={`Risk: ${computedRR.riskPct}%`}
                  />
                  <div
                    style={{ width: `${Math.min(90, Math.max(50, 100 - (100 / (1 + parseFloat(computedRR.rr.split(':')[0] || 1)))))}%` }}
                    className="bg-emerald-500 dark:bg-[#00FF88] h-full transition-all"
                    title={`Reward: ${computedRR.rewardPct}%`}
                  />
                </div>
                <div className="flex items-center justify-between text-[10px] font-mono mt-1 font-semibold">
                  <span className="text-rose-600 dark:text-[#FF3B30]">Risk ₹{computedRR.risk} ({computedRR.riskPct}%)</span>
                  <span className="text-emerald-600 dark:text-[#00FF88]">Reward ₹{computedRR.reward} (+{computedRR.rewardPct}%)</span>
                </div>
              </div>
            ) : (
              <div className={`p-2 rounded-lg text-xs flex items-center gap-1.5 ${
                isLight ? "bg-rose-50 border border-rose-200 text-rose-700" : "bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30]"
              }`}>
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>{computedRR.error || "Please enter valid numeric price levels."}</span>
              </div>
            )}
          </div>

          {/* Admin Insight / Remark & Quick Chips */}
          <div className="space-y-2">
            <Label className={`text-[11px] font-mono uppercase flex items-center gap-1.5 ${
              isLight ? "text-slate-700 font-semibold" : "text-[#94A3B8]"
            }`}>
              <ShieldCheck className="w-3.5 h-3.5 text-amber-500 dark:text-[#FFCC00]" /> Admin Insight / Trade Remark (Optional)
            </Label>
            
            <div className="flex flex-wrap gap-1.5">
              {[
                "⚡ Strict SL trail after T1 hit",
                "🎯 High conviction institutional zone",
                "🚀 Breakout momentum confirmation",
                "⏳ Wait for 15M candle close retest",
                "🛡️ Safe risk-defined swing setup"
              ].map(chip => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => setForm(p => ({ ...p, admin_remark: chip }))}
                  className={`text-[10px] font-mono px-2 py-0.5 rounded-md border transition-colors cursor-pointer ${
                    isLight
                      ? "bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 border-slate-200"
                      : "bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white border-white/5"
                  }`}
                >
                  {chip}
                </button>
              ))}
            </div>

            <Input
              value={form.admin_remark}
              onChange={e => setForm(p => ({ ...p, admin_remark: e.target.value }))}
              placeholder="e.g. High conviction zone. Trail SL strictly once T1 is reached."
              className={`text-xs h-9 transition-all ${
                isLight
                  ? "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-purple-500"
                  : "bg-[#12121A] border-white/10 text-white focus:border-[#00FF88]"
              }`}
            />
          </div>

          {/* Modal Actions */}
          <div className={`flex flex-col-reverse sm:flex-row items-center justify-between gap-2 pt-3 border-t ${
            isLight ? "border-slate-100" : "border-white/10"
          }`}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={saving}
              className={`text-xs w-full sm:w-auto h-9 font-semibold ${
                isLight ? "text-slate-600 hover:text-slate-900 hover:bg-slate-100" : "text-gray-400 hover:text-white"
              }`}
            >
              Cancel
            </Button>

            <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
              {isAlreadyApproved ? (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => handleSave(false)}
                  disabled={saving || !computedRR.isValid}
                  className={`font-bold text-xs h-9 px-4.5 rounded-xl shadow-md flex items-center gap-1.5 ${
                    isLight
                      ? "bg-emerald-600 hover:bg-emerald-500 text-white shadow-emerald-600/20"
                      : "bg-[#00FF88] text-black hover:brightness-110 shadow-[#00FF88]/20"
                  }`}
                >
                  {saving ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                      Save &amp; Update Live Levels
                    </>
                  )}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleSave(false)}
                    disabled={saving || !computedRR.isValid}
                    className={`text-xs h-9 px-3.5 font-medium ${
                      isLight
                        ? "border-slate-300 text-slate-700 hover:bg-slate-100"
                        : "border-white/15 text-white hover:bg-white/10"
                    }`}
                  >
                    Save Draft
                  </Button>

                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleSave(true)}
                    disabled={saving || !computedRR.isValid}
                    className={`font-bold text-xs h-9 px-4.5 rounded-xl shadow-md flex items-center gap-1.5 ${
                      isLight
                        ? "bg-emerald-600 hover:bg-emerald-500 text-white shadow-emerald-600/20"
                        : "bg-[#00FF88] text-black hover:brightness-110 shadow-[#00FF88]/20"
                    }`}
                  >
                    {saving ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <>
                        <Zap className="w-3.5 h-3.5 fill-current" />
                        Approve &amp; Publish Live
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
